import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { StatusBadge } from "../Badges/StatusBadge";
import { statusScheduled, statusNew } from "@design-system";
import {
  SplitTable,
  SplitTableRef,
  DropdownButton,
  Button,
  OrderTemplateDetails,
} from "@components";
import { useWorkspaceData } from "@data";
import { Customer, Driver, OrderTemplate } from "../../models";
import {
  HiOutlineChevronDown,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi";
import { HiPause, HiPlay } from "react-icons/hi2";

export interface OrderTemplatesListProps {
  templates: OrderTemplate[];
  /** Owner/Administrator: may edit, delete and pause/resume templates. */
  canManage?: boolean;
  /** Non-ReadOnly: may start an order from a template. */
  canCreateOrder?: boolean;
  onEdit: (template: OrderTemplate) => void;
  onDelete: (template: OrderTemplate) => void;
  onCreateOrder: (template: OrderTemplate) => void;
  /** Toggle a recurring template's active flag (pause/resume generation). */
  onToggleRecurrence?: (template: OrderTemplate) => void;
  /** Notifies the page when the detail split opens/closes (layout hook). */
  isOpen?: (open: boolean) => void;
}

/**
 * Split-view listing of order templates: a table on the left and a full
 * template detail panel on the right, matching the orders / locations pages.
 * All row actions live in the detail panel's actions dropdown.
 */
export const OrderTemplatesList: FC<OrderTemplatesListProps> = (props) => {
  const { t } = useTranslation(["orders", "common"]);
  const { drivers } = useWorkspaceData();
  const navigate = useNavigate();
  const location = useLocation();
  const splitTableRef = useRef<SplitTableRef>(null);

  const [openedTemplateId, setOpenedTemplateId] = useState<string | null>(null);
  const [hideColumns, setHideColumns] = useState(false);

  // The page also uses this state to choose its main CTA. Reset it when a
  // refetch or navigation unmounts the list.
  useEffect(() => () => props.isOpen?.(false), [props.isOpen]);

  const openedTemplate = useMemo(
    () => props.templates.find((tpl) => tpl.id === openedTemplateId),
    [props.templates, openedTemplateId],
  );

  const getDriverName = (driverId?: string): string => {
    if (!driverId) return t("templates.no_driver");
    const driver = drivers?.find((d: Driver) => d.email === driverId);
    return driver ? Driver.getDriverName(driver) : driverId;
  };

  const updateOpenedTemplate = (template?: OrderTemplate) => {
    if (template) {
      setOpenedTemplateId(template.id);
      setHideColumns(true);
      props.isOpen?.(true);
      navigate(`/order-templates?template=${template.id}`, { replace: true });
      splitTableRef.current?.setSplit(true);
      return;
    }
    setOpenedTemplateId(null);
    setHideColumns(false);
    props.isOpen?.(false);
    navigate(`/order-templates`, { replace: true });
    splitTableRef.current?.setSplit(false);
  };

  // Deep-link / restore selection from the ?template=<id> URL param.
  useEffect(() => {
    const templateParam = new URLSearchParams(location.search).get("template");
    if (!templateParam) return;
    const match = props.templates.find((tpl) => tpl.id === templateParam);
    if (match) {
      updateOpenedTemplate(match);
    } else if (props.templates.length > 0) {
      navigate(`/order-templates`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, props.templates]);

  // Close the split if the opened template disappears (deleted / filtered out).
  useEffect(() => {
    if (
      openedTemplateId &&
      !props.templates.some((tpl) => tpl.id === openedTemplateId)
    ) {
      updateOpenedTemplate(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.templates, openedTemplateId]);

  const tableHeaders = [
    {
      id: "name",
      children: t("common:name"),
      showOnSmallScreen: true,
    },
    {
      id: "customer",
      children: t("templates.details.customer"),
      showOnSmallScreen: true,
    },
    {
      id: "pickup",
      children: t("templates.details.pickup_address"),
      hidden: hideColumns,
    },
    {
      id: "delivery",
      children: t("templates.details.delivery_address"),
      hidden: hideColumns,
    },
    {
      id: "driver",
      children: t("templates.details.driver"),
      hidden: hideColumns,
    },
  ];

  const tableRows = useMemo(() => {
    return props.templates.map((template) => {
      const isRecurring = !!template.recurrence;
      const isActive = template.recurrence?.active === true;
      const customerName =
        Customer.getCustomerName(template.customer) ||
        t("common:unknown_customer");

      // Follow the same route order and legacy pickup fallback as the timeline.
      const route = template.route ?? [];
      const pickup = route.find((stop, index) => stop.isPickup ?? index === 0);
      const delivery = route.reduce<(typeof route)[number] | undefined>(
        (last, stop, index) => ((stop.isPickup ?? index === 0) ? last : stop),
        undefined,
      );
      const address = (stop?: (typeof route)[number]) =>
        stop?.location?.addressLine || stop?.location?.displayName || "—";

      return {
        id: template.id,
        isActive: template.id === openedTemplateId,
        onRowClickData: template,
        cells: [
          {
            id: "name",
            showOnSmallScreen: true,
            children: (
              <div className="flex flex-wrap items-center gap-2 whitespace-normal break-words max-w-[14rem]">
                <span className="font-medium text-text">{template.name}</span>
                {isRecurring && (
                  <StatusBadge
                    status={
                      isActive
                        ? t("templates.recurring_badge")
                        : t("templates.paused_badge")
                    }
                    bgStyle={isActive ? statusScheduled : statusNew}
                  />
                )}
              </div>
            ),
          },
          {
            id: "customer",
            showOnSmallScreen: true,
            children: (
              <span className="block whitespace-normal break-words max-w-[12rem]">
                {customerName}
              </span>
            ),
          },
          ...[
            {
              id: "pickup",
              stop: pickup,
              hint: t("templates.details.first_pickup"),
            },
            {
              id: "delivery",
              stop: delivery,
              hint: t("templates.details.last_delivery"),
            },
          ].map(({ id, stop, hint }) => ({
            id,
            hidden: hideColumns,
            children: (
              <span
                className="block whitespace-normal break-words max-w-[16rem]"
                title={`${hint}: ${address(stop)}`}
              >
                {address(stop)}
              </span>
            ),
          })),
          {
            id: "driver",
            hidden: hideColumns,
            children: (
              <span className="truncate">
                {getDriverName(template.driverId)}
              </span>
            ),
          },
        ],
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.templates, openedTemplateId, hideColumns, drivers, t]);

  // Only expose actions the current role can actually perform, mirroring the
  // cloud-api authorization so we never present an action that fails on submit.
  const canManage = props.canManage ?? false;
  const canCreateOrder = props.canCreateOrder ?? false;

  const actionItems = openedTemplate
    ? [
        ...(canManage && openedTemplate.recurrence && props.onToggleRecurrence
          ? [
              {
                text: (
                  <span className="flex items-center gap-2 p-4 w-52">
                    {openedTemplate.recurrence.active ? (
                      <HiPause className="h-4 w-4" />
                    ) : (
                      <HiPlay className="h-4 w-4" />
                    )}
                    {openedTemplate.recurrence.active
                      ? t("templates.pause")
                      : t("templates.resume")}
                  </span>
                ),
                onClick: () => props.onToggleRecurrence?.(openedTemplate),
              },
            ]
          : []),
        ...(canManage
          ? [
              {
                text: (
                  <span className="flex items-center gap-2 p-4 w-52">
                    <HiOutlinePencil className="h-4 w-4" />
                    {t("common:edit")}
                  </span>
                ),
                onClick: () => props.onEdit(openedTemplate),
              },
              {
                text: (
                  <span className="flex items-center gap-2 p-4 w-52 text-lgb-red-300 hover:text-lgb-red-200 dark:text-lgb-red-200 dark:hover:text-lgb-red-300">
                    <HiOutlineTrash className="h-4 w-4" />
                    {t("common:delete")}
                  </span>
                ),
                onClick: () => props.onDelete(openedTemplate),
              },
            ]
          : []),
      ]
    : [];

  return (
    <SplitTable
      ref={splitTableRef}
      searchable={false}
      isLoading={false}
      tableRows={tableRows}
      tableHeaders={tableHeaders}
      onRowClick={(template: OrderTemplate | undefined) =>
        updateOpenedTemplate(template)
      }
      content={<OrderTemplateDetails template={openedTemplate} />}
      topRightContent={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {openedTemplate && canCreateOrder && (
            <Button
              variant="primary"
              size="md"
              onClick={() => props.onCreateOrder(openedTemplate)}
            >
              {t("templates.use_template")}
            </Button>
          )}
          {openedTemplate && actionItems.length > 0 && (
            <DropdownButton
              placement="bottom-end"
              buttonVariant="secondary"
              buttonSize="md"
              className="!ml-0"
              buttonText={t("common:actions")}
              buttonAppendIcon={<HiOutlineChevronDown className="ml-2" />}
              items={actionItems}
            />
          )}
        </div>
      }
    />
  );
};

export default OrderTemplatesList;
